/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import React, { useState } from 'react';
import {
  Box,
  styled,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';
import { PresetMediaItem } from 'src/pages/MachineGroupDetails/UpsertPresetForm';
import MediaTileComponent from './MediaTileComponent';

const DropZone = styled(Box)(({ theme }) => ({
  transition: 'all 0.2s ease-in-out',
  '&.drop-target': {
    backgroundColor: theme.palette.primary.light,
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
  },
}));

export type FolderItem = {
  id: string;
  customerId: string;
  name: string;
  mediaCount: number;
};

interface FolderAccordionProps {
  folder: FolderItem & { media: PresetMediaItem[] };
  isExpanded: boolean;
  onToggle: () => void;
  onDeleteMedia: (media: PresetMediaItem) => void;
  onShowPresets: (media: PresetMediaItem) => void;
  onMoveMedia: (
    mediaId: string,
    folderId: string | null,
    mediaName: string,
  ) => void;
  onDeleteFolder: (folderId: string, folderName: string) => void;
}

function FolderAccordion({
  folder,
  isExpanded,
  onToggle,
  onDeleteMedia,
  onShowPresets,
  onMoveMedia,
  onDeleteFolder,
}: FolderAccordionProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const { mediaId, mediaName } = data;
      onMoveMedia(mediaId, folder.id, mediaName);
    } catch (error) {
      console.error('Error parsing drag data:', error);
    }
  };

  return (
    <DropZone
      className={isDragOver ? 'drop-target' : ''}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Accordion expanded={isExpanded} onChange={onToggle}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            <FolderIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">{folder.name}</Typography>
            <Chip
              label={`${folder.media.length} files`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Box sx={{ marginLeft: 'auto' }}>
              <Tooltip
                title={
                  folder.media.length > 0
                    ? 'Cannot delete folder that contains media files'
                    : 'Delete folder'
                }
              >
                <span>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteFolder(folder.id, folder.name);
                    }}
                    size="small"
                    disabled={folder.media.length > 0}
                    sx={{
                      color:
                        folder.media.length > 0
                          ? 'action.disabled'
                          : 'error.main',
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {folder.media.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
              }}
            >
              {folder.media.map((media) => (
                <MediaTileComponent
                  key={media.fullFileUrl}
                  media={media}
                  onDelete={onDeleteMedia}
                  onShowPresets={onShowPresets}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 100,
                textAlign: 'center',
              }}
            >
              <FolderIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No media files in this folder
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </DropZone>
  );
}

export default FolderAccordion;
